<template>
  <div class="py-4 container-fluid dashboard">
    <!-- Header con filtros mejorado -->
    <div class="row mb-4">
      <div class="col-12 animate-fade-in-down">
        <div class="dashboard-header-card liquid-glass hover-lift">
          <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div class="dashboard-title d-flex align-items-center">
              <i class="ri-dashboard-line me-2 text-primary"></i>
              <h5 class="mb-0 fw-semibold">Panel de Control</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vista personalizable con widgets -->
    <div v-if="showCustomDashboard" class="row mb-4">
      <div class="col-12">
        <WidgetGrid @widget-data-request="handleWidgetDataRequest" />
      </div>
    </div>
    
    <!-- Vista personalizable - Comparativas históricas -->
    <div v-if="showCustomDashboard" class="row mb-4">
      <div class="col-12">
        <HistoricalComparison @data-request="handleHistoricalDataRequest" />
      </div>
    </div>
    
    <!-- Vista clásica del dashboard -->
    <div v-if="!showCustomDashboard">
      <!-- Tarjetas estadísticas: 3 recuadros con animación -->
      <div class="row stagger-children">
      <div v-for="(card, index) in estadisticasCards" :key="`stat-card-${index}`" class="col-xl-4 col-sm-6 mb-xl-0 mb-4">
        <transition name="scale-fade" appear>
          <mini-statistics-card
            :title="card.title"
            :value="isLoading ? 'Cargando...' : card.value()"
            :percentage="card.percentage()"
            :icon="card.icon"
            direction-reverse
            class="stat-card hover-lift will-change-transform"
          />
        </transition>
      </div>
    </div>

    <!-- Fila de gráficos con mejoras visuales -->
    <div class="row mt-4">
      <!-- Gráfico circular interactivo: Distribución de Proyectos -->
      <div class="col-lg-6 mb-4 animate-fade-in-left">
        <InteractiveChart
          title="Distribución de Obras por Proyecto"
          subtitle="Distribución proporcional de obras en cada proyecto"
          chart-type="doughnut"
          :data="doughnutChartData"
          :options="doughnutChartOptions"
          :interactive="true"
          @chart-click="onChartClick"
          @chart-refresh="loadDashboardData"
        />
      </div>
      
      <!-- Gráfico de barras interactivo: Total obras por Proyecto -->
      <div class="col-lg-6 mb-4 animate-fade-in-right">
        <InteractiveChart
          title="Total Obras por Proyecto"
          subtitle="Comparación del número total de obras por proyecto"
          chart-type="bar"
          :data="barChartData"
          :options="barChartOptions"
          :interactive="true"
          @chart-click="onChartClick"
          @chart-refresh="loadDashboardData"
        />
      </div>
    </div>
    
    </div> <!-- Cerrar vista clásica -->
    

  </div>
</template>

<script>
import { ref, computed, onMounted, reactive, watchEffect, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import WidgetGrid from "@/components/WidgetGrid.vue";
import InteractiveChart from "@/components/InteractiveChart.vue";
import HistoricalComparison from "@/components/HistoricalComparison.vue";
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
// Importar plugin de datalabels para los gráficos
import 'chartjs-plugin-datalabels';

export default {
  name: "dashboard-default",
  components: {
    MiniStatisticsCard,
    WidgetGrid,
    InteractiveChart,
    HistoricalComparison,
  },
  setup() {
    // Router
    const router = useRouter();
    
    // Estados básicos
    const iconBackground = ref("bg-gradient-primary");
    const isLoading = ref(false);
    const tableLoading = ref(false);
    const dateRange = ref([]);
    const searchQuery = ref('');
    const filtroEstado = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalProyectos = ref(0);
    const colorMap = ref({});
    
    // Nuevas funcionalidades del dashboard mejorado
    const showCustomDashboard = ref(false);
    const realTimeUpdates = ref(false);
    const realTimeIcon = ref('ri-wifi-line');
    const showExportModal = ref(false);
    const exportLoading = ref(false);
    const realTimeInterval = ref(null);
    
    // Configuración de exportación
    const exportConfig = reactive({
      type: 'projects',
      format: 'xlsx',
      dateRange: [],
      includeCharts: true
    });
    
    // Tipos de exportación rápida
    const quickExportTypes = ref([
      {
        type: 'projects-excel',
        label: 'Proyectos Excel',
        icon: 'ri-file-excel-line',
        loading: false
      },
      {
        type: 'dashboard-pdf',
        label: 'Dashboard PDF',
        icon: 'ri-file-pdf-line',
        loading: false
      },
      {
        type: 'charts-png',
        label: 'Gráficos PNG',
        icon: 'ri-image-line',
        loading: false
      }
    ]);
    
    // Datos del dashboard (usando reactive para mejor rendimiento)
    const dashboardData = reactive({
      totalProyectos: 0,
      porcentajeProyectos: '+0%',
      totalSubproyectos: 0,
      porcentajeSubproyectos: '+0%',
      totalObras: 0,
      porcentajeObras: '+0%',
      proyectos: [],
      distribucionProyectos: {
        labels: [],
        datos: []
      },
      obrasPorProyecto: {
        labels: [],
        datos: []
      }
    });

    // Reactive dark mode detection
    const isDarkMode = ref(false);
    
    // Watch for theme changes
    const updateTheme = () => {
      const newValue = document.body.classList.contains('dark-mode-active');
      isDarkMode.value = newValue;
    };
    
    // Initial theme detection - ejecutar inmediatamente
    updateTheme();
    
    // Watch for class changes on body
    // const observer = new MutationObserver(updateTheme); // Comentado para evitar problemas en Chrome

    // Estados disponibles para los proyectos
    const estadosProyecto = [
      { value: 'Completado', label: 'Completado' },
      { value: 'En proceso', label: 'En proceso' },
      { value: 'Planificado', label: 'Planificado' },
      { value: 'Cancelado', label: 'Cancelado' },
    ];

    // Configuración de las tarjetas estadísticas
    const estadisticasCards = [
      {
        title: "Total Proyectos",
        value: () => dashboardData.totalProyectos.toString(),
        percentage: () => ({
          value: dashboardData.porcentajeProyectos || '+0%',
          color: dashboardData.porcentajeProyectos?.startsWith('+') ? 'text-success' : 'text-danger'
        }),
        icon: {
          component: 'ri-funds-line',
          background: 'bg-gradient-primary'
        }
      },
      {
        title: "Subproyectos",
        value: () => dashboardData.totalSubproyectos.toString(),
        percentage: () => ({
          value: dashboardData.porcentajeSubproyectos || '+0%',
          color: dashboardData.porcentajeSubproyectos?.startsWith('+') ? 'text-success' : 'text-danger'
        }),
        icon: {
          component: 'ri-building-line',
          background: 'bg-gradient-info'
        }
      },
      {
        title: "Obras Registradas",
        value: () => dashboardData.totalObras.toString(),
        percentage: () => ({
          value: dashboardData.porcentajeObras || '+0%',
          color: dashboardData.porcentajeObras?.startsWith('+') ? 'text-success' : 'text-danger'
        }),
        icon: {
          component: 'ri-hammer-line',
          background: 'bg-gradient-success'
        }
      }
    ];

    // Gráfico de dona (distribucion de obras)
    const doughnutChartData = computed(() => {
      return {
        labels: dashboardData.distribucionProyectos.labels,
        datasets: [
          {
            data: dashboardData.distribucionProyectos.datos,
            backgroundColor: [
              "#4361ee", "#7209b7", "#f72585", "#4cc9f0", "#06d6a0", 
              "#118ab2", "#ffbe0b", "#fb5607", "#3a86ff", "#8338ec",
              "#ff006e", "#38b000", "#ff595e", "#1982c4", "#6a4c93"
            ],
            borderWidth: 2,
            borderColor: '#ffffff',
            hoverOffset: 10,
            hoverBorderWidth: 4,
            hoverBorderColor: '#ffffff'
          }
        ]
      };
    });

    const doughnutChartOptions = computed(() => {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: {
            position: 'bottom',
            align: 'center',
            labels: {
              padding: 15,
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12,
                weight: 'bold'
              },
              color: isDarkMode.value ? '#e2e8f0' : '#2d3748'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(20, 20, 20, 0.9)',
            padding: 12,
            cornerRadius: 8,
            bodyFont: {
              size: 13,
              family: "'Roboto', sans-serif"
            },
            titleFont: {
              size: 14,
              weight: 'bold',
              family: "'Roboto', sans-serif"
            },
            displayColors: true,
            boxWidth: 8,
            boxHeight: 8,
            boxPadding: 4,
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.raw || 0;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} obras (${percentage}%)`;
              },
              labelTextColor: function() {
                return '#ffffff';
              }
            }
          },
          animation: {
            animateScale: true,
            animateRotate: true,
            duration: 1200,
            easing: 'easeOutQuart'
          },
          datalabels: {
            display: false,
            color: '#ffffff',
            font: {
              weight: 'bold',
              size: 11
            }
          }
        }
      };
    });

    // Gráfico de barras (total obras por proyecto) - ACTUALIZADO
    const barChartData = computed(() => {
      const backgroundColors = [
        'rgba(67, 97, 238, 0.85)', 
        'rgba(114, 9, 183, 0.85)', 
        'rgba(247, 37, 133, 0.85)', 
        'rgba(76, 201, 240, 0.85)', 
        'rgba(6, 214, 160, 0.85)', 
        'rgba(17, 138, 178, 0.85)',
        'rgba(255, 190, 11, 0.85)',
        'rgba(251, 86, 7, 0.85)'
      ];

      const hoverBackgroundColors = [
        'rgba(67, 97, 238, 1)', 
        'rgba(114, 9, 183, 1)', 
        'rgba(247, 37, 133, 1)', 
        'rgba(76, 201, 240, 1)', 
        'rgba(6, 214, 160, 1)', 
        'rgba(17, 138, 178, 1)',
        'rgba(255, 190, 11, 1)',
        'rgba(251, 86, 7, 1)'
      ];

      return {
        labels: dashboardData.obrasPorProyecto.labels,
        datasets: [{
          label: "Total Obras",
          data: dashboardData.obrasPorProyecto.datos,
          backgroundColor: backgroundColors.slice(0, dashboardData.obrasPorProyecto.labels.length),
          hoverBackgroundColor: hoverBackgroundColors.slice(0, dashboardData.obrasPorProyecto.labels.length),
          borderRadius: 8,
          borderSkipped: false,
          borderWidth: 1,
          borderColor: 'rgba(255, 255, 255, 0.5)',
          hoverBorderWidth: 2,
          hoverBorderColor: '#ffffff'
        }]
      };
    });

    // Opciones actualizadas para el nuevo BarChart
    const barChartOptions = computed(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      // Eliminar o cambiar a 'x' para barras verticales
      // indexAxis: 'y', 
      plugins: {
        legend: {
          position: 'top', // Cambiado de 'right' a 'top' para mejor visualización con barras verticales
          align: 'center',
          labels: {
            padding: 15,
            usePointStyle: true,
            pointStyle: 'rectRounded',
            boxWidth: 10,
            boxHeight: 10,
            font: {
              size: 11,
              weight: 'bold'
            },
            color: isDarkMode.value ? '#e2e8f0' : '#2d3748'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(20, 20, 20, 0.9)',
          padding: 12,
          cornerRadius: 8,
          bodyFont: {
            size: 13,
            family: "'Roboto', sans-serif"
          },
          titleFont: {
            size: 14,
            weight: 'bold',
            family: "'Roboto', sans-serif"
          },
          displayColors: true,
          boxWidth: 8,
          boxHeight: 8,
          boxPadding: 4,
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.raw || 0;
              return `${label}: ${value} obras`;
            },
            labelTextColor: function() {
              return '#ffffff';
            }
          }
        },
        datalabels: {
          display: true,
          align: 'center',
          anchor: 'end',
          color: '#333333',
          font: {
            weight: 'bold',
            size: 12
          },
          formatter: function(value) {
            return value;
          },
          // Ajustar posición para barras verticales
          offset: -25,
          rotation: 0
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 11
            },
            color: isDarkMode.value ? '#e2e8f0' : '#2d3748',
            // Etiquetas inclinadas para nombres largos
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          grid: {
            color: isDarkMode.value ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            drawBorder: false
          },
          ticks: {
            font: {
              size: 11
            },
            color: isDarkMode.value ? '#e2e8f0' : '#2d3748',
            padding: 10
          },
          beginAtZero: true
        }
      },
      animation: {
        animateScale: true,
        animateRotate: false, // Cambiado a false para barras verticales
        duration: 1200,
        easing: 'easeOutQuart'
      }
    };
  });

    // Proyectos filtrados
    const filteredProyectos = computed(() => {
      if (!dashboardData.proyectos) return [];
      
      // Primero filtramos por búsqueda
      const proyectosFiltrados = dashboardData.proyectos.filter(proyecto => {
        return searchQuery.value === '' ||
          (proyecto.nombre && proyecto.nombre.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (proyecto.tipo && proyecto.tipo.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
          (proyecto.prefijo && proyecto.prefijo.toLowerCase().includes(searchQuery.value.toLowerCase()));
      });
      
      // Luego ordenamos con prioridad personalizada
      return proyectosFiltrados.sort((a, b) => {
        // Definir prioridades para proyectos específicos
        const getPrioridad = (nombre) => {
          if (nombre === '2019-202X CELLNEX ING e INST') return 1;
          if (nombre === '2019-202X COMM') return 2;
          return 3; // Todos los demás proyectos
        };
        
        const prioridadA = getPrioridad(a.nombre);
        const prioridadB = getPrioridad(b.nombre);
        
        // Primero ordenar por prioridad
        if (prioridadA !== prioridadB) {
          return prioridadA - prioridadB;
        }
        
        // Si tienen la misma prioridad, ordenar alfabéticamente
        if (a.nombre !== b.nombre) return a.nombre.localeCompare(b.nombre);
        if (a.tipo && b.tipo && a.tipo !== b.tipo) return a.tipo.localeCompare(b.tipo);
        if (a.prefijo && b.prefijo && a.prefijo !== b.prefijo) return a.prefijo.localeCompare(b.prefijo);
        return 0;
      });
    });

    // Color aleatorio para avatares
    const getRandomColor = (text) => {
      if (!text) return '#cccccc'; // Color por defecto para valores vacíos
      
      if (!colorMap.value[text]) {
        const colors = [
          '#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0', 
          '#4895ef', '#560bad', '#f77f00', '#d62828', '#06d6a0'
        ];
        colorMap.value[text] = colors[Math.floor(Math.random() * colors.length)];
      }
      return colorMap.value[text];
    };

    // Cargar datos del dashboard
    const loadDashboardData = async () => {
      try {
        isLoading.value = true;
        
        // Preparar filtros de fecha
        let params = {};
        if (dateRange.value && dateRange.value.length === 2) {
          params.fechaInicio = dateRange.value[0];
          params.fechaFin = dateRange.value[1];
        }
        
        // Añadir parámetros de paginación
        params.page = currentPage.value;
        params.pageSize = pageSize.value;
        
        // Obtener datos de estadísticas desde PostgreSQL
        const estadisticasResponse = await axios.get('/api/v2/dashboard/estadisticas', { params });
        
        // Verificar si tenemos los datos necesarios
        if (estadisticasResponse.data && estadisticasResponse.data.totales) {
          const totales = estadisticasResponse.data.totales;
          
          // Actualizar valores estadísticos
          dashboardData.totalProyectos = totales.totalProyectos;
          dashboardData.totalSubproyectos = totales.totalSubproyectos;
          dashboardData.totalObras = totales.totalObras;
          
          // Para los porcentajes, establecemos valores predeterminados
          dashboardData.porcentajeProyectos = '+0%';
          dashboardData.porcentajeSubproyectos = '+0%';
          dashboardData.porcentajeObras = '+0%';
          
          // Actualizar datos del gráfico circular
          if (estadisticasResponse.data.distribucionProyectos) {
            dashboardData.distribucionProyectos = estadisticasResponse.data.distribucionProyectos;
          }
          
          // Actualizar datos del gráfico de barras
          if (estadisticasResponse.data.obrasPorProyecto) {
            dashboardData.obrasPorProyecto = estadisticasResponse.data.obrasPorProyecto;
          }
        }
        
        // Obtener el resto de datos para el dashboard (tabla, etc.)
        const dashboardResponse = await axios.get('/api/v2/dashboard', { params });
        
        // Actualizar datos de la tabla
        if (dashboardResponse.data) {
          dashboardData.proyectos = dashboardResponse.data.proyectos || [];
          
          // Solo usar distribucionProyectos y obrasPorProyecto del endpoint dashboard
          // si no los tenemos ya de estadisticas
          if (!dashboardData.distribucionProyectos.labels.length && dashboardResponse.data.distribucionProyectos) {
            dashboardData.distribucionProyectos = dashboardResponse.data.distribucionProyectos;
          }
          
          if (!dashboardData.obrasPorProyecto.labels.length && dashboardResponse.data.obrasPorProyecto) {
            dashboardData.obrasPorProyecto = dashboardResponse.data.obrasPorProyecto;
          }
          
          // Actualizar total para paginación
          totalProyectos.value = dashboardResponse.data.totalProyectosCount || dashboardData.proyectos.length;
        }
        
        ElMessage({
          message: 'Datos actualizados correctamente',
          type: 'success',
          duration: 2000
        });
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
        ElMessage({
          message: 'Error al cargar los datos. Intente nuevamente.',
          type: 'error',
          duration: 3000
        });
      } finally {
        isLoading.value = false;
      }
    };

    // Formatters optimizados con memoización
    const formatCache = {
      currency: {},
      date: {}
    };

    const formatCurrency = (value) => {
      // Memoización para evitar formatear el mismo valor repetidamente
      const cacheKey = String(value);
      if (!formatCache.currency[cacheKey]) {
        formatCache.currency[cacheKey] = new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN',
          minimumFractionDigits: 0
        }).format(value);
      }
      return formatCache.currency[cacheKey];
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      
      // Memoización para fechas
      if (!formatCache.date[dateString]) {
        const date = new Date(dateString);
        formatCache.date[dateString] = new Intl.DateTimeFormat('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(date);
      }
      return formatCache.date[dateString];
    };

    const getEstadoTag = (estado) => {
      const estados = {
        'Completado': 'success',
        'En proceso': 'warning',
        'Planificado': 'info',
        'Cancelado': 'danger',
      };
      return estados[estado] || 'info';
    };

    // Métodos para destacar filas en la tabla
    const tableRowClassName = ({ row }) => {
      if (row.estado === 'Cancelado') {
        return 'canceled-row';
      }
      return '';
    };

    const handleRowClick = (row) => {
      console.log('Fila seleccionada:', row);
      // Aquí puedes implementar alguna acción al hacer clic en la fila
    };

    // Métodos de navegación mejorados usando Vue Router
    const verDetalles = (id) => {
      tableLoading.value = true;
      router.push(`/proyectos/${id}`);
    };

    const editarProyecto = (id) => {
      tableLoading.value = true;
      router.push(`/proyectos/${id}/editar`);
    };

    // Nuevos métodos para las acciones adicionales
    const exportarProyecto = (id) => {
      ElMessage({
        message: `Exportando proyecto ${id}...`,
        type: 'info'
      });
      // Implementar lógica de exportación aquí
    };

    const duplicarProyecto = (id) => {
      ElMessage({
        message: `Duplicando proyecto ${id}...`,
        type: 'info'
      });
      // Implementar lógica de duplicación aquí
    };

    const eliminarProyecto = (id) => {
      ElMessageBox.confirm(
        '¿Está seguro de eliminar este proyecto? Esta acción no se puede deshacer.',
        'Advertencia',
        {
          confirmButtonText: 'Eliminar',
          cancelButtonText: 'Cancelar',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(() => {
        // Aquí iría la lógica para eliminar el proyecto
        ElMessage({
          type: 'success',
          message: `Proyecto ${id} eliminado correctamente`
        });
      }).catch(() => {
        // Cancelado
      });
    };

    // Método para exportar gráficos como imágenes
    const exportarGrafico = async (chartId) => {
      try {
        const chartElement = document.getElementById(chartId);
        if (!chartElement) {
          ElMessage.error('No se pudo encontrar el gráfico');
          return;
        }
        
        // Mostrar mensaje de carga
        ElMessage({
          message: 'Exportando gráfico...',
          type: 'info',
          duration: 1000
        });
        
        await nextTick();
        const canvas = await html2canvas(chartElement);
        
        // Crear enlace para descargar
        const link = document.createElement('a');
        link.download = `grafico-${chartId}-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        ElMessage({
          message: 'Gráfico exportado correctamente',
          type: 'success'
        });
      } catch (error) {
        console.error('Error al exportar gráfico:', error);
        ElMessage.error('Error al exportar el gráfico');
      }
    };

    // Manejo de paginación
    const handlePageChange = (page) => {
      currentPage.value = page;
      loadDashboardData();
    };

    const handleSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1; // Reset to first page
      loadDashboardData();
    };

    // Observamos cambios en la búsqueda para filtrar los resultados localmente
    watchEffect(() => {
      if (searchQuery.value !== undefined) {
        // No necesitamos recargar los datos, solo filtramos localmente
      }
    });

    // Nuevos métodos para funcionalidades mejoradas
    
    // Toggle dashboard view
    const toggleDashboardView = () => {
      ElMessage({
        message: showCustomDashboard.value ? 'Vista personalizada activada' : 'Vista clásica activada',
        type: 'info',
        duration: 2000
      });
    };
    
    // Cambio de rango de fechas
    const onDateRangeChange = (dates) => {
      if (dates && dates.length === 2) {
        loadDashboardData();
        ElMessage({
          message: 'Datos filtrados por rango de fechas',
          type: 'success',
          duration: 2000
        });
      }
    };
    
    // Toggle de actualizaciones en tiempo real
    const toggleRealTimeUpdates = () => {
      if (realTimeUpdates.value) {
        realTimeIcon.value = 'ri-wifi-line';
        // realTimeInterval.value = setInterval(() => { // Comentado para evitar problemas en Chrome
        //   loadDashboardData();
        // }, 30000); // Actualizar cada 30 segundos
        
        ElMessage({
          message: 'Actualizaciones en tiempo real activadas',
          type: 'success',
          duration: 2000
        });
      } else {
        realTimeIcon.value = 'ri-wifi-off-line';
        if (realTimeInterval.value) {
          clearInterval(realTimeInterval.value);
          realTimeInterval.value = null;
        }
        
        ElMessage({
          message: 'Actualizaciones en tiempo real desactivadas',
          type: 'info',
          duration: 2000
        });
      }
    };
    
    // Manejar solicitudes de datos de widgets
    const handleWidgetDataRequest = async (request) => {
      try {
        // Aquí puedes implementar la lógica para obtener datos específicos para cada widget
        console.log('Widget data request:', request);
        
        // Simular carga de datos
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        ElMessage({
          message: 'Datos del widget actualizados',
          type: 'success',
          duration: 2000
        });
      } catch (error) {
        console.error('Error al cargar datos del widget:', error);
        ElMessage.error('Error al cargar datos del widget');
      }
    };
    
    // Exportación rápida
    const quickExport = async (exportType) => {
      // Encontrar el índice del tipo de exportación en el array
      const typeIndex = quickExportTypes.value.findIndex(t => t.type === exportType.type);
      if (typeIndex === -1) return;
      
      // Usar el objeto del array directamente para evitar condiciones de carrera
      const targetType = quickExportTypes.value[typeIndex];
      targetType.loading = true;
      
      try {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simular exportación
        
        switch (exportType.type) {
          case 'projects-excel':
            await exportProjectsToExcel();
            break;
          case 'dashboard-pdf':
            await exportDashboardToPDF();
            break;
          case 'charts-png':
            await exportChartsAsPNG();
            break;
        }
        
        ElMessage.success(`${exportType.label} exportado correctamente`);
      } catch (error) {
        console.error('Error en exportación:', error);
        ElMessage.error('Error al exportar datos');
      } finally {
        // Restablecer el loading usando el objeto del array
        targetType.loading = false;
      }
    };
    
    // Exportar proyectos a Excel
    const exportProjectsToExcel = async () => {
      const workbook = XLSX.utils.book_new();
      const worksheetData = [
        ['Proyecto', 'Subproyecto', 'Prefijo', 'Último Cod. Obra'],
        ...dashboardData.proyectos.map(p => [
          p.nombre || '',
          p.tipo || '',
          p.prefijo || '',
          p.maxnumcodobra || ''
        ])
      ];
      
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Proyectos');
      
      const fileName = `proyectos-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    };
    
    // Exportar dashboard a PDF
    const exportDashboardToPDF = () => {
      return new Promise((resolve, reject) => {
        const dashboard = document.querySelector('.dashboard');
        if (dashboard) {
          html2canvas(dashboard, {
            height: window.innerHeight,
            width: window.innerWidth,
            scrollX: 0,
            scrollY: 0
          }).then(canvas => {
            const link = document.createElement('a');
            link.download = `dashboard-${new Date().toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            resolve();
          }).catch(reject);
        } else {
          reject(new Error('Dashboard element not found'));
        }
      });
    };
    
    // Exportar gráficos como PNG
    const exportChartsAsPNG = async () => {
      const charts = document.querySelectorAll('.chart-animation');
      
      for (let i = 0; i < charts.length; i++) {
        const chart = charts[i];
        const canvas = await html2canvas(chart);
        
        const link = document.createElement('a');
        link.download = `grafico-${i + 1}-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        // Pequeña pausa entre descargas
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    };
    
    // Exportar reporte personalizado
    const exportReport = async () => {
      exportLoading.value = true;
      
      try {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simular exportación
        
        switch (exportConfig.format) {
          case 'xlsx':
            await exportProjectsToExcel();
            break;
          case 'pdf':
            await exportDashboardToPDF();
            break;
          case 'csv':
            await exportToCSV();
            break;
        }
        
        showExportModal.value = false;
        ElMessage.success('Reporte exportado correctamente');
      } catch (error) {
        console.error('Error al exportar reporte:', error);
        ElMessage.error('Error al exportar reporte');
      } finally {
        exportLoading.value = false;
      }
    };
    
    // Exportar a CSV
    const exportToCSV = () => {
      const csvData = dashboardData.proyectos.map(p => 
        `"${p.nombre || ''}","${p.tipo || ''}","${p.prefijo || ''}","${p.maxnumcodobra || ''}"`
      ).join('\n');
      
      const csvContent = 'Proyecto,Subproyecto,Prefijo,Último Cod. Obra\n' + csvData;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `proyectos-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    };
    
    // Manejar click en gráficos interactivos
    const onChartClick = (data) => {
      console.log('Chart clicked:', data);
      
      // Mostrar información detallada del elemento clickeado
      const message = `Has seleccionado: ${data.label} con valor ${data.value}`;
      ElMessage({
        message,
        type: 'info',
        duration: 3000
      });
      
      // Aquí podrías navegar a una vista detallada o abrir un modal
      // router.push(`/proyectos/${data.label}`);
    };
    
    // Manejar solicitudes de datos históricos
    const handleHistoricalDataRequest = async (request) => {
      try {
        console.log('Historical data request:', request);
        
        // Simular carga de datos históricos
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        ElMessage({
          message: 'Datos históricos actualizados',
          type: 'success',
          duration: 2000
        });
      } catch (error) {
        console.error('Error al cargar datos históricos:', error);
        ElMessage.error('Error al cargar datos históricos');
      }
    };


    // Ciclo de vida
    onMounted(() => {
      // Inicializar observer para cambios de tema
      // observer.observe(document.body, { attributes: true, attributeFilter: ['class'] }); // Comentado para evitar problemas en Chrome
      
      loadDashboardData();
      
      // Configurar cache de colores para avatares
      dashboardData.proyectos?.forEach(proyecto => {
        getRandomColor(proyecto.nombre);
      });
      
      // Event listener para redimensionar gráficos cuando cambie el tamaño de la ventana
      const handleResize = () => {
        // Aquí podrías implementar lógica adicional para redimensionar los gráficos si es necesario
      };
      
      window.addEventListener('resize', handleResize);
      
      // Limpiar event listener al desmontar
      return () => {
        window.removeEventListener('resize', handleResize);
        // observer.disconnect(); // Comentado para evitar problemas en Chrome
      };
    });
    
    return {
      // Estados
      iconBackground,
      isLoading,
      tableLoading,
      dateRange,
      dashboardData,
      searchQuery,
      filtroEstado,
      estadosProyecto,
      currentPage,
      pageSize,
      totalProyectos,
      estadisticasCards,
      
      // Nuevas funcionalidades
      showCustomDashboard,
      realTimeUpdates,
      realTimeIcon,
      showExportModal,
      exportLoading,
      exportConfig,
      quickExportTypes,
      
      // Computed
      doughnutChartData,
      doughnutChartOptions,
      barChartData,
      barChartOptions,
      filteredProyectos,
      
      // Métodos originales
      loadDashboardData,
      formatCurrency,
      formatDate,
      getEstadoTag,
      verDetalles,
      editarProyecto,
      exportarProyecto,
      duplicarProyecto,
      eliminarProyecto,
      handlePageChange,
      handleSizeChange,
      tableRowClassName,
      handleRowClick,
      getRandomColor,
      exportarGrafico,
      isDarkMode,
      
      // Nuevos métodos
      toggleDashboardView,
      onDateRangeChange,
      toggleRealTimeUpdates,
      handleWidgetDataRequest,
      handleHistoricalDataRequest,
      onChartClick,
      quickExport,
      exportReport
    };
  }
};
</script>

<style>
/* Estilos globales para el dashboard */
.dashboard {
  min-height: 100vh;
}

/* Glassmorphism Effects */
.glassmorphism-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  transition: all 0.3s ease;
}

.glassmorphism-card:hover {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.25);
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.glassmorphism-header {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

/* Animaciones y efectos para tarjetas */
.card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}

/* Estilo específico para tarjetas de estadísticas */
.stat-card {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px) saturate(150%);
  -webkit-backdrop-filter: blur(15px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

/* Estilos para tarjetas de gráficos - MEJORADOS */
.chart-card {
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-card .card-header {
  border-bottom: 1px solid rgba(0,0,0,0.05);
  padding-bottom: 15px;
}

.chart-card .card-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.chart-card .chart-actions i {
  font-size: 18px;
  cursor: pointer;
  margin-left: 8px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.chart-card .chart-actions i:hover {
  opacity: 1;
}

.chart-animation {
  animation: fadeInUp 0.5s ease-out;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  visibility: visible;
}

/* Añadir efecto de brillo al hacer hover en gráficos */
.chart-card:hover .card-body::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  opacity: 0;
  animation: pulse 2s infinite;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 0;
  }
}

/* Estilos para el header del dashboard */
.dashboard-header-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  color: white;
  margin-bottom: 20px;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

/* Forzar el gradiente azul en modo claro */
body:not(.dark-mode-active) .dashboard-header-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
}

.dashboard-header-card .card-body {
  padding: 1.2rem;
}

.dashboard-title {
  display: flex;
  align-items: center;
}

.dashboard-title i {
  font-size: 24px;
  margin-right: 10px;
}

.dashboard-title h5 {
  color: white;
  margin: 0;
  font-weight: 600;
}

.dashboard-header-card .el-date-picker {
  --el-border-radius-base: 6px;
}

/* Tabla de proyectos */
.projects-card {
  margin-bottom: 30px;
}

.projects-card .el-table {
  --el-table-border-color: transparent;
  --el-table-header-bg-color: #f8f9fa;
  --el-table-row-hover-bg-color: #f1f4ff;
  border-radius: 8px;
  overflow: hidden;
}

.projects-card .el-table th {
  font-weight: 600;
  padding: 12px 8px;
}

.projects-card .el-table td {
  padding: 12px 8px;
}

.action-buttons {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}

.action-buttons .el-button {
  padding: 6px;
}

.canceled-row {
  background-color: rgba(245, 108, 108, 0.1);
}

/* Animaciones */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== DARK MODE STYLES ===== */
body.dark-mode-active .glassmorphism-card {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .glassmorphism-card:hover {
  background: rgba(26, 32, 44, 0.8);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

body.dark-mode-active .glassmorphism-header {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .dashboard-header-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

body.dark-mode-active .chart-card {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

body.dark-mode-active .projects-card {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

body.dark-mode-active .card-header h6 {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .text-primary {
  color: #4fc3f7 !important;
}

body.dark-mode-active .text-secondary {
  color: #b0bec5 !important;
}

body.dark-mode-active .text-muted {
  color: rgba(255, 255, 255, 0.6) !important;
}

body.dark-mode-active .el-table {
  background: rgba(26, 32, 44, 0.4);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
}

body.dark-mode-active .el-table th {
  background: rgba(26, 32, 44, 0.6);
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-table td {
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: rgba(255, 255, 255, 0.02);
}

body.dark-mode-active .el-table tbody tr:hover > td {
  background: rgba(79, 195, 247, 0.1);
}

body.dark-mode-active .el-avatar {
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
}

body.dark-mode-active .el-tag {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px) saturate(150%);
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .el-input__wrapper {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-input__wrapper:hover {
  border-color: rgba(79, 195, 247, 0.5);
}

body.dark-mode-active .el-input__wrapper.is-focus {
  border-color: #4fc3f7;
  box-shadow: 0 0 0 2px rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .el-input__inner {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .el-input__inner::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

body.dark-mode-active .el-pagination {
  background: rgba(26, 32, 44, 0.4);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-radius: 8px;
  padding: 10px;
}

body.dark-mode-active .el-pagination button {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-pagination button:hover:not(:disabled) {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

body.dark-mode-active .el-pagination .btn-next,
body.dark-mode-active .el-pagination .btn-prev {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
}

body.dark-mode-active .el-pagination .btn-next:hover,
body.dark-mode-active .el-pagination .btn-prev:hover {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

body.dark-mode-active .el-pager li {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
}

body.dark-mode-active .el-pager li:hover {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

body.dark-mode-active .el-pager li.is-active {
  background: #4fc3f7;
  color: #1a202c;
}

body.dark-mode-active .el-dropdown-menu {
  background: rgba(26, 32, 44, 0.9);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-dropdown-menu__item {
  color: rgba(255, 255, 255, 0.8);
}

body.dark-mode-active .el-dropdown-menu__item:hover {
  background: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

body.dark-mode-active .el-tooltip__popper {
  background: rgba(26, 32, 44, 0.9);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .el-tooltip__popper .el-tooltip__arrow::before {
  background: rgba(26, 32, 44, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Enhanced Chart Styling for Dark Mode */
body.dark-mode-active .chart-animation {
  filter: brightness(1.1) contrast(1.1);
}

/* Chart text colors for dark mode */
body.dark-mode-active .chart-animation canvas {
  filter: brightness(1.2) contrast(1.1);
}

/* Override chart legend and axis colors in dark mode */
body.dark-mode-active .chart-card .chart-animation {
  --chart-text-color: #e2e8f0;
  --chart-grid-color: rgba(255, 255, 255, 0.1);
}

/* Light mode chart colors */
body:not(.dark-mode-active) .chart-card .chart-animation {
  --chart-text-color: #2d3748;
  --chart-grid-color: rgba(0, 0, 0, 0.05);
}

/* Enhanced Chart Styling for Light Mode */
body:not(.dark-mode-active) .chart-animation {
  filter: brightness(1) contrast(1);
  opacity: 1;
  visibility: visible;
}

/* Chart canvas colors for light mode */
body:not(.dark-mode-active) .chart-animation canvas {
  filter: brightness(1) contrast(1);
  opacity: 1;
  visibility: visible;
}

body.dark-mode-active .spinner-border {
  color: #4fc3f7;
}

/* Dark mode for statistics cards */
body.dark-mode-active .stat-card {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(15px) saturate(150%);
  -webkit-backdrop-filter: blur(15px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

body.dark-mode-active .stat-card:hover {
  background: rgba(26, 32, 44, 0.8);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

/* Estilos para los nuevos controles del dashboard */
.dashboard-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Contenedores de gráficos con altura fija */
.row .col-lg-6 .interactive-chart-container,
.row .col-12 .interactive-chart-container {
  height: 500px;
  max-height: 500px;
}

/* Asegurar que los gráficos no se estiren */
.interactive-chart-container {
  display: flex;
  flex-direction: column;
}

.interactive-chart-container .chart-content {
  flex: 1;
  overflow: hidden;
}

.dashboard-controls .el-date-picker {
  min-width: 250px;
}

.dashboard-controls .el-switch {
  --el-switch-on-color: #4361ee;
  --el-switch-off-color: #dcdfe6;
}

/* Estilos para el panel de exportación */
.export-panel {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.export-panel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.25);
}

.export-panel .card-header {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 12px 16px;
}

.export-panel .card-header h6 {
  margin: 0;
  font-weight: 600;
  color: inherit;
}

.export-quick-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.export-quick-actions .el-button {
  background: rgba(67, 97, 238, 0.1);
  border: 1px solid rgba(67, 97, 238, 0.2);
  color: #4361ee;
  transition: all 0.2s ease;
}

.export-quick-actions .el-button:hover {
  background: rgba(67, 97, 238, 0.2);
  border-color: #4361ee;
  transform: translateY(-1px);
}

/* Estilos para modales mejorados */
.el-dialog {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.el-dialog__header {
  background: rgba(67, 97, 238, 0.1);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(67, 97, 238, 0.2);
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
}

.el-dialog__title {
  color: #4361ee;
  font-weight: 600;
}

.el-dialog__body {
  padding: 20px;
}

/* Dark mode para nuevos elementos */
body.dark-mode-active .export-panel {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .export-panel:hover {
  background: rgba(26, 32, 44, 0.8);
  border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .export-panel .card-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .export-panel .card-header h6 {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .export-quick-actions .el-button {
  background: rgba(79, 195, 247, 0.1);
  border-color: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
}

body.dark-mode-active .export-quick-actions .el-button:hover {
  background: rgba(79, 195, 247, 0.2);
  border-color: #4fc3f7;
}

body.dark-mode-active .el-dialog {
  background: rgba(26, 32, 44, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .el-dialog__header {
  background: rgba(79, 195, 247, 0.1);
  border-bottom-color: rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .el-dialog__title {
  color: #4fc3f7;
}

/* Animaciones para transiciones */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Mejoras responsivas avanzadas */
@media (max-width: 1200px) {
  .dashboard-controls {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .dashboard-controls .el-date-picker {
    min-width: 200px;
  }
  
  .widget-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
}

@media (max-width: 992px) {
  .dashboard .card-body {
    padding: 1rem;
  }
  
  .action-buttons {
    flex-wrap: wrap;
  }
  
  .dashboard-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .dashboard-controls .el-date-picker {
    min-width: auto;
    width: 100%;
  }
  
  .dashboard-header-card .card-body {
    padding: 1rem;
  }
  
  .dashboard-title h5 {
    font-size: 1.1rem;
  }
  
  /* Ajustar tamaño de gráficos en tablets */
  .interactive-chart-container {
    height: 400px;
  }
  
  /* Estadísticas cards responsive */
  .stat-card {
    margin-bottom: 1rem;
  }
  
  /* Tabla responsive */
  .projects-card .el-table {
    font-size: 0.875rem;
  }
  
  .projects-card .el-table th,
  .projects-card .el-table td {
    padding: 8px 6px;
  }
}

@media (max-width: 768px) {
  .dashboard {
    padding: 0.5rem;
  }
  
  .dashboard .row > div {
    margin-bottom: 1rem;
  }
  
  .dashboard-header-card .card-body {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .dashboard-title {
    margin-bottom: 0;
  }
  
  .dashboard-title h5 {
    font-size: 1rem;
  }
  
  .dashboard-controls {
    width: 100%;
  }
  
  .dashboard-controls .el-switch {
    margin: 4px 0;
  }
  
  /* Gráficos en móvil */
  .interactive-chart-container {
    height: 350px;
  }
  
  .custom-legend {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  
  .legend-item {
    padding: 4px 6px;
    font-size: 11px;
  }
  
  /* Export panel móvil */
  .export-panel .card-header {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .export-quick-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .export-quick-actions .el-button {
    width: 100%;
    justify-content: center;
  }
  
  /* Widgets grid móvil */
  .widget-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }
  
  .widget-toolbar {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .toolbar-right {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  /* Tabla proyectos móvil */
  .projects-card .card-header {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .projects-card .el-input {
    width: 100% !important;
  }
  
  .projects-card .el-table {
    font-size: 0.75rem;
  }
  
  .projects-card .el-table th,
  .projects-card .el-table td {
    padding: 6px 4px;
  }
  
  .projects-card .el-table-column {
    min-width: 80px;
  }
  
  /* Ocultar algunas columnas en móvil */
  .projects-card .el-table .el-table__cell:nth-child(n+4) {
    display: none;
  }
  
  /* Paginación móvil */
  .el-pagination {
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }
  
  .el-pagination .el-pagination__total,
  .el-pagination .el-pagination__sizes {
    order: 3;
    width: 100%;
    text-align: center;
    margin-top: 8px;
  }
  
  /* Comparativas históricas móvil */
  .comparison-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .header-controls {
    flex-direction: column;
    gap: 8px;
  }
  
  .change-indicators {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .change-indicator {
    padding: 8px 12px;
  }
  
  .indicator-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
  
  .indicator-values {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  
  .current-value {
    font-size: 14px;
  }
  
  .change-value {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .dashboard {
    padding: 0.25rem;
  }
  
  .dashboard-header-card {
    margin-bottom: 0.75rem;
  }
  
  .dashboard-header-card .card-body {
    padding: 0.75rem;
  }
  
  .dashboard-title h5 {
    font-size: 0.9rem;
  }
  
  /* Gráficos muy pequeños */
  .interactive-chart-container {
    height: 300px;
  }
  
  .chart-header {
    padding: 8px 12px;
  }
  
  .chart-title-section .chart-title {
    font-size: 14px;
  }
  
  .chart-title-section .chart-subtitle {
    font-size: 10px;
  }
  
  /* Stats cards muy pequeñas */
  .stat-card {
    min-height: 80px;
  }
  
  /* Botones más pequeños */
  .el-button {
    padding: 6px 8px;
    font-size: 11px;
  }
  
  .el-button.el-button--small {
    padding: 4px 6px;
    font-size: 10px;
  }
  
  /* Modal adaptativo */
  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
  
  .el-dialog__body {
    padding: 12px;
  }
  
  /* Formularios más compactos */
  .el-form-item {
    margin-bottom: 12px;
  }
  
  .el-form-item__label {
    font-size: 12px;
    padding-bottom: 4px;
  }
}

/* Mejoras para dispositivos táctiles */
@media (hover: none) and (pointer: coarse) {
  .hover-lift:hover {
    transform: none;
  }
  
  .chart-card:hover {
    transform: none;
  }
  
  .draggable-widget:hover {
    transform: none;
  }
  
  /* Aumentar área táctil */
  .el-button,
  .legend-item,
  .widget-option {
    min-height: 44px;
    min-width: 44px;
  }
  
  .chart-controls .el-button {
    padding: 8px 12px;
  }
}
</style>